import { useEffect, useRef } from 'react';

/**
 * Calls `onVisible` when the returned ref element enters (or nears) the viewport.
 * Uses IntersectionObserver with a root margin so images start loading just before scroll-in.
 */
export function useLazyInView(onVisible, options = {}) {
  const {
    rootMargin = '200px 0px',
    threshold = 0,
    once = true,
    disabled = false,
  } = options;

  const elementRef = useRef(null);
  const onVisibleRef = useRef(onVisible);
  const hasTriggeredRef = useRef(false);

  useEffect(() => {
    onVisibleRef.current = onVisible;
  }, [onVisible]);

  useEffect(() => {
    if (disabled) return undefined;

    const node = elementRef.current;
    if (!node) return undefined;

    if (once && hasTriggeredRef.current) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (once && hasTriggeredRef.current) return;

          hasTriggeredRef.current = true;
          onVisibleRef.current?.();

          if (once) {
            observer.disconnect();
          }
        });
      },
      { rootMargin, threshold },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [disabled, once, rootMargin, threshold]);

  return elementRef;
}

export default useLazyInView;
